export type ReportsToolbarMenuEntry =
  | { type: 'item'; id: string; label: string }
  | { type: 'divider'; id: string }

/**
 * Time period when Latest Data = Latest Data (current periods only).
 * Order: This Year → This Month → This Week → Today.
 */
export const REPORTS_TOOLBAR_PERIOD_MENU_LATEST: ReportsToolbarMenuEntry[] = [
  { type: 'item', id: 'this-year', label: 'This Year' },
  { type: 'item', id: 'this-month', label: 'This Month' },
  { type: 'item', id: 'this-week', label: 'This Week' },
  { type: 'item', id: 'today', label: 'Today' },
]

/**
 * Time period when Latest Data = Historical.
 */
export const REPORTS_TOOLBAR_PERIOD_MENU_HISTORICAL: ReportsToolbarMenuEntry[] =
  [
    { type: 'item', id: 'last-year', label: 'Last Year' },
    { type: 'item', id: 'last-month', label: 'Last Month' },
    { type: 'item', id: 'last-week', label: 'Last Week' },
    { type: 'item', id: 'yesterday', label: 'Yesterday' },
    { type: 'item', id: 'last-90-days', label: 'Last 90 days' },
    { type: 'item', id: 'last-60-days', label: 'Last 60 days' },
    { type: 'item', id: 'last-30-days', label: 'Last 30 days' },
    { type: 'item', id: 'last-7-days', label: 'Last 7 days' },
    { type: 'item', id: 'last-9-months', label: 'Last 9 months' },
    { type: 'item', id: 'last-6-months', label: 'Last 6 months' },
    { type: 'item', id: 'last-3-months', label: 'Last 3 months' },
    { type: 'item', id: 'calendar-q1', label: 'Q1 (Jan–Mar)' },
    { type: 'item', id: 'calendar-q2', label: 'Q2 (Apr–Jun)' },
    { type: 'item', id: 'calendar-q3', label: 'Q3 (Jul–Sep)' },
    { type: 'item', id: 'calendar-q4', label: 'Q4 (Oct–Dec)' },
    { type: 'item', id: 'date-range', label: 'Date Range' },
  ]

/** @see REPORTS_TOOLBAR_DEFAULT_SELECTION */
export const PERIOD_DEFAULT_LATEST = 'this-month' as const
export const PERIOD_DEFAULT_HISTORICAL = 'last-90-days' as const

/** Resolves the period column menu from period category (column `period-category`). */
export function getPeriodToolbarMenuForLatestData(
  periodCategoryValue: string | undefined,
): ReportsToolbarMenuEntry[] {
  if (periodCategoryValue === 'historical') {
    return REPORTS_TOOLBAR_PERIOD_MENU_HISTORICAL
  }
  return REPORTS_TOOLBAR_PERIOD_MENU_LATEST
}

function periodMenuItemIds(
  periodCategoryValue: string | undefined,
): Set<string> {
  return new Set(
    getPeriodToolbarMenuForLatestData(periodCategoryValue)
      .filter(
        (e): e is Extract<ReportsToolbarMenuEntry, { type: 'item' }> =>
          e.type === 'item',
      )
      .map((e) => e.id),
  )
}

export function isPeriodIdAllowedForLatestData(
  periodId: string,
  periodCategoryValue: string | undefined,
): boolean {
  if (!periodId) return false
  return periodMenuItemIds(periodCategoryValue).has(periodId)
}

/** If stored period id is not valid for the current period category, use this. */
export function getDefaultPeriodForLatestData(
  periodCategoryValue: string | undefined,
): string {
  return periodCategoryValue === 'historical'
    ? PERIOD_DEFAULT_HISTORICAL
    : PERIOD_DEFAULT_LATEST
}

/** Options per toolbar column (`ReportsFilterOption.id`). Excludes `period-preset`; use `getPeriodToolbarMenuForLatestData`. */
export const REPORTS_TOOLBAR_MENUS: Record<string, ReportsToolbarMenuEntry[]> =
  {
    'period-category': [
      { type: 'item', id: 'latest-data', label: 'Latest Data' },
      { type: 'item', id: 'historical', label: 'Historical' },
      { type: 'item', id: 'snapshot', label: 'Snapshot' },
      { type: 'item', id: 'comparison-mode', label: 'Comparison mode' },
    ],
    'provider-filter': [
      { type: 'item', id: 'all-providers', label: 'All Providers' },
    ],
    /** Snapshot mode: as-of hour (12:00 AM through 11:00 PM). */
    'snapshot-time': [
      { type: 'item', id: '12am', label: '12:00 AM' },
      { type: 'item', id: '1am', label: '1:00 AM' },
      { type: 'item', id: '2am', label: '2:00 AM' },
      { type: 'item', id: '3am', label: '3:00 AM' },
      { type: 'item', id: '4am', label: '4:00 AM' },
      { type: 'item', id: '5am', label: '5:00 AM' },
      { type: 'item', id: '6am', label: '6:00 AM' },
      { type: 'item', id: '7am', label: '7:00 AM' },
      { type: 'item', id: '8am', label: '8:00 AM' },
      { type: 'item', id: '9am', label: '9:00 AM' },
      { type: 'item', id: '10am', label: '10:00 AM' },
      { type: 'item', id: '11am', label: '11:00 AM' },
      { type: 'item', id: '12pm', label: '12:00 PM' },
      { type: 'item', id: '1pm', label: '1:00 PM' },
      { type: 'item', id: '2pm', label: '2:00 PM' },
      { type: 'item', id: '3pm', label: '3:00 PM' },
      { type: 'item', id: '4pm', label: '4:00 PM' },
      { type: 'item', id: '5pm', label: '5:00 PM' },
      { type: 'item', id: '6pm', label: '6:00 PM' },
      { type: 'item', id: '7pm', label: '7:00 PM' },
      { type: 'item', id: '8pm', label: '8:00 PM' },
      { type: 'item', id: '9pm', label: '9:00 PM' },
      { type: 'item', id: '10pm', label: '10:00 PM' },
      { type: 'item', id: '11pm', label: '11:00 PM' },
    ],
    /** Comparison mode (table header): year scope. */
    'comparison-year': [
      { type: 'item', id: 'last-current', label: 'Last–Current' },
      { type: 'item', id: 'choose-years', label: 'Choose years' },
    ],
    'comparison-month': [
      { type: 'item', id: 'last-current', label: 'Last–Current' },
      { type: 'item', id: 'choose-months', label: 'Choose months' },
    ],
    'comparison-week': [
      { type: 'item', id: 'last-current', label: 'Last–Current' },
      { type: 'item', id: 'choose-weeks', label: 'Choose weeks' },
    ],
    'comparison-day': [
      { type: 'item', id: 'last-current', label: 'Last–Current' },
      { type: 'item', id: 'choose-days', label: 'Choose days' },
    ],
    'comparison-provider': [
      { type: 'item', id: 'all-providers', label: 'All Providers' },
      { type: 'divider', id: 'div-comp-prov' },
      { type: 'item', id: 'dr-michael-carter', label: 'Dr. Michael Carter' },
      { type: 'item', id: 'dr-james-patel', label: 'Dr. James Patel' },
    ],
  }

/** Default selected value id for each column (matches first chip on each report). */
export const REPORTS_TOOLBAR_DEFAULT_SELECTION: Record<string, string> = {
  'period-category': 'latest-data',
  'period-preset': 'this-month',
  'provider-filter': 'all-providers',
  'snapshot-time': '6am',
  'snapshot-as-of': '',
  'hist-range-start': '',
  'hist-range-end': '',
  'comparison-year': 'last-current',
  'comparison-month': 'last-current',
  'comparison-week': 'last-current',
  'comparison-day': 'last-current',
  'comparison-provider': 'all-providers',
}

export type GetReportsToolbarMenuOptions = {
  /** Required for the period column (`period-preset`): resolves menu from Latest Data vs Historical. */
  latestDataValue?: string
}

export type ReportsToolbarProviderSource = {
  provider_id: string
  first_name: string
  last_name: string
}

/** Live provider list for the All Providers toolbar column (ids = provider_id UUIDs). */
export function buildAllProvidersToolbarMenu(
  providers: ReportsToolbarProviderSource[],
): ReportsToolbarMenuEntry[] {
  const items: ReportsToolbarMenuEntry[] = [
    { type: 'item', id: 'all-providers', label: 'All Providers' },
  ]
  if (providers.length > 0) {
    items.push({ type: 'divider', id: 'div-providers-live' })
    for (const provider of providers) {
      items.push({
        type: 'item',
        id: provider.provider_id,
        label: `${provider.first_name} ${provider.last_name}`,
      })
    }
  }
  return items
}

export function getReportsToolbarMenu(
  filterId: string,
  fallbackLabel: string,
  options?: GetReportsToolbarMenuOptions,
  providerDropdownOptions?: ReportsToolbarMenuEntry[],
): ReportsToolbarMenuEntry[] {
  if (filterId === 'period-preset') {
    return getPeriodToolbarMenuForLatestData(options?.latestDataValue)
  }
  if (filterId === 'provider-filter') {
    return providerDropdownOptions ?? []
  }
  return (
    REPORTS_TOOLBAR_MENUS[filterId] ?? [
      { type: 'item', id: filterId, label: fallbackLabel },
    ]
  )
}

/** Previous snapshot-time ids; keep labels so old saved selection still reads correctly. */
export const LEGACY_SNAPSHOT_TIME_LABELS: Record<string, string> = {
  '6am-5pm': '6:00 AM',
  '7am-6pm': '7:00 AM',
  '8am-5pm': '8:00 AM',
}

/** Old comparison dimension value ids (before Last–Current / Choose … pattern). */
export const LEGACY_COMPARISON_VALUE_LABELS: Partial<
  Record<string, Record<string, string>>
> = {
  'comparison-month': {
    'comp-month-1': 'January',
    'comp-month-2': 'February',
    'comp-month-3': 'March',
    'comp-month-4': 'April',
  },
  'comparison-week': {
    'comp-week-1': 'Week 1',
    'comp-week-2': 'Week 2',
    'comp-week-3': 'Week 3',
    'comp-week-4': 'Week 4',
  },
  'comparison-day': {
    'comp-day-1': 'Mon',
    'comp-day-2': 'Tue',
    'comp-day-3': 'Wed',
    'comp-day-4': 'Thu',
  },
}

export function getReportsToolbarMenuItemLabel(
  filterId: string,
  valueId: string,
  fallbackLabel: string,
  latestDataValue?: string,
  providerDropdownOptions?: ReportsToolbarMenuEntry[],
): string {
  const menu = getReportsToolbarMenu(
    filterId,
    fallbackLabel,
    {
      latestDataValue,
    },
    providerDropdownOptions,
  )
  const item = menu.find(
    (e): e is Extract<ReportsToolbarMenuEntry, { type: 'item' }> =>
      e.type === 'item' && e.id === valueId,
  )
  if (item) return item.label
  if (filterId === 'snapshot-time' && valueId in LEGACY_SNAPSHOT_TIME_LABELS) {
    return LEGACY_SNAPSHOT_TIME_LABELS[valueId]!
  }
  const legacyComp = LEGACY_COMPARISON_VALUE_LABELS[filterId]?.[valueId]
  if (legacyComp) return legacyComp
  return fallbackLabel
}
